import { getRangeLabel } from "../../components/discoveryRangeFilters.shared";
import { useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { Drawer, Icon } from "../../../../components";
import { Button, H5, IconType } from "@sellar-io/components";
import {
  abvFilterType,
  AbvType,
  dietaryTypes,
  useDiscoveryV2SearchStore,
} from "../DiscoveryV2SearchStore";
import { FilterAbvCard } from "../filterCards/FilterAbvCard";
import { FilterSwitch } from "../filterCards/FilterCard";
import { FilterContainerCard } from "../filterCards/FilterContainerCard";
import { FilterDietaryCard } from "../filterCards/FilterDietaryCard";
import { FilterPriceCard } from "../filterCards/FilterPriceCard";
import {
  getPriceTypeCategory,
  PriceTypeCategory,
} from "../filterCards/FilterPriceCard.shared";
import {
  draughtPriceFilterType,
  packagedPriceFilterType,
} from "../filterCards/FilterPriceTypes";
import { FilterStyleCard } from "../filterCards/FilterStyleCard";
import { FilterSupplierCard } from "../filterCards/FilterSupplierCard";
import { SellarIconGradient } from "@sellar-io/components";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { useCanShowDeliveredBySellarOptionV2 } from "../lib/useCanShowDeliveredBySellarOptionV2";
import { DeliveredBySellarOptionV2Mobile } from "../lib/DeliveredBySellarOptionV2";
import { twMerge } from "tailwind-merge";
import { OrderBySelector } from "../lib/OrderBySelector";

type FilterPageType =
  | "style"
  | "container"
  | "abv"
  | "dietary"
  | "price"
  | "supplier"
  | undefined;
export const DiscoveryMobileFilterDrawer = ({
  setDrawerActive,
  drawerActive,
  onFilterChanged,
}: {
  drawerActive: boolean;
  setDrawerActive: (drawerActive: boolean) => void;
  onFilterChanged: () => void;
}) => {
  const { selectedContainerTypes, reset } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      selectedContainerTypes: state.selectedContainerTypes,
      reset: state.reset,
    })),
  );
  const [filterPage, setFilterPage] = useState<FilterPageType>();
  const derivedPriceTypeCategory = getPriceTypeCategory(selectedContainerTypes);
  const [priceTypeCategory, setPriceTypeCategory] = useState<
    PriceTypeCategory | undefined
  >(derivedPriceTypeCategory);
  const [previousContainerTypes, setPreviousContainerTypes] = useState(
    selectedContainerTypes,
  );

  if (selectedContainerTypes !== previousContainerTypes) {
    setPreviousContainerTypes(selectedContainerTypes);
    setPriceTypeCategory(derivedPriceTypeCategory);
  }
  const renderContent = () => {
    switch (filterPage) {
      case "style":
        return <FilterStyleCard onClick={() => setFilterPage(undefined)} />;
      case "container":
        return <FilterContainerCard onClick={() => setFilterPage(undefined)} />;
      case "abv":
        return <FilterAbvCard onClick={() => setFilterPage(undefined)} />;
      case "dietary":
        return <FilterDietaryCard onClick={() => setFilterPage(undefined)} />;
      case "price":
        return (
          <FilterPriceCard
            priceTypeCategory={priceTypeCategory}
            setPriceTypeCategory={setPriceTypeCategory}
            onClick={() => setFilterPage(undefined)}
          />
        );
      case "supplier":
        return (
          <FilterSupplierCard
            onCloseFilterPage={() => setFilterPage(undefined)}
          />
        );
      default:
        return (
          <FilterDrawerMainPage
            priceTypeCategory={priceTypeCategory}
            setFilterPage={setFilterPage}
          />
        );
    }
  };
  return (
    <Drawer
      sticky={false}
      setActive={setDrawerActive}
      active={drawerActive}
      position={"right"}
    >
      <div onChangeCapture={onFilterChanged}>{renderContent()}</div>
      <div className="sticky bottom-0 mx-auto mt-auto flex w-full flex-col gap-2 bg-white p-4">
        {filterPage ? (
          <Button className={"w-full"} onClick={() => setFilterPage(undefined)}>
            Back
          </Button>
        ) : (
          <div className={"grid grid-cols-2 gap-2"}>
            <div className="col-span-1 w-full">
              <Button
                onClick={() => {
                  reset();
                  onFilterChanged();
                }}
                className={"w-full"}
                color={"secondary"}
              >
                Clear all
              </Button>
            </div>
            <div className="col-span-1 w-full">
              <Button
                className={"w-full"}
                onClick={() => setDrawerActive(false)}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

const FilterDrawerMainPage = ({
  setFilterPage,
  priceTypeCategory,
}: {
  setFilterPage: (page: FilterPageType) => void;
  priceTypeCategory: PriceTypeCategory | undefined;
}) => {
  const canShowDeliveredBySellarOptionV2 =
    useCanShowDeliveredBySellarOptionV2();

  const {
    isMySuppliersOnly,
    setIsMySuppliersOnly,
    isInStockItemsOnly,
    setIsInStockItemsOnly,
    selectedContainerTypes,
    selectedAbvTypes,
    abvRange,
    selectedDietaryTypes,
    selectedPriceTypes,
    styleSearchValues,
    isDirectDelivery,
    setIsDirectDelivery,
    isSellarCreditEligibleOnly,
    setIsSellarCreditEligibleOnly,
    isDeliveredBySellar,
    selectedSuppliers,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      searchValue: state.searchValue,
      setSelectedProductIds: state.setSelectedProductIds,
      setIsMySuppliersOnly: state.setIsMySuppliersOnly,
      isMySuppliersOnly: state.isMySuppliersOnly,
      isInStockItemsOnly: state.isInStockItemsOnly,
      setIsInStockItemsOnly: state.setIsInStockItemsOnly,
      selectedContainerTypes: state.selectedContainerTypes,
      selectedAbvTypes: state.selectedAbvTypes,
      abvRange: state.abvRange,
      selectedDietaryTypes: state.selectedDietaryTypes,
      selectedPriceTypes: state.selectedPriceTypes,
      styleSearchValues: state.styleSearchValues,
      isDirectDelivery: state.isDirectDelivery,
      setIsDirectDelivery: state.setIsDirectDelivery,
      isSellarCreditEligibleOnly: state.isSellarCreditEligibleOnly,
      setIsSellarCreditEligibleOnly: state.setIsSellarCreditEligibleOnly,
      isDeliveredBySellar: state.isDeliveredBySellar,
      selectedSuppliers: state.selectedSuppliers,
    })),
  );
  const { business } = useSellarCurrentBusiness();

  const sellarCreditEnabled = useRemoteFeatureFlags().isActive(
    "retailerSellarCreditCheckout",
  );

  const canShowSellarCreditOption =
    sellarCreditEnabled && business?.BusinessUser?.canAccessSellarCredit;

  return (
    <div className={"flex flex-col"}>
      <FilterDrawerHeader title={"Sort & Filter"} icon={"FILTER_VARIANT"} />
      <div
        className={twMerge(
          "border-solid border-gray-300 p-4",
          canShowDeliveredBySellarOptionV2 ? "border-b-none" : "border-b",
        )}
      >
        <OrderBySelector />
      </div>
      {!isDeliveredBySellar && !canShowDeliveredBySellarOptionV2 && (
        <div className={"border-b border-solid border-gray-300 p-4"}>
          <FilterSwitch
            title={"Suppliers delivering direct"}
            checked={isDirectDelivery}
            onChange={(checked) => setIsDirectDelivery(checked)}
          />
        </div>
      )}

      {canShowDeliveredBySellarOptionV2 && <DeliveredBySellarOptionV2Mobile />}

      <div className={"border-b border-solid border-gray-300 p-4"}>
        <FilterSwitch
          title={"My Suppliers only"}
          checked={isMySuppliersOnly}
          onChange={(checked) => setIsMySuppliersOnly(checked)}
        />
      </div>
      <div className={"border-b border-solid border-gray-300 p-4"}>
        <FilterSwitch
          title={"In-stock items only"}
          checked={isInStockItemsOnly}
          onChange={(checked) => setIsInStockItemsOnly(checked)}
        />
      </div>
      {canShowSellarCreditOption && (
        <div className={"border-b border-solid border-gray-300 p-4"}>
          <FilterSwitch
            title={
              <div className="flex gap-2">
                <SellarIconGradient />
                <span className="bg-linear-to-br from-[#B557F1] to-[#D213A6] bg-clip-text font-semibold text-transparent">
                  Sellar Credit
                </span>
                only
              </div>
            }
            checked={isSellarCreditEligibleOnly}
            onChange={(checked) => setIsSellarCreditEligibleOnly(checked)}
          />
        </div>
      )}
      <FilterDrawerItem
        title={"STYLE"}
        onClick={() => setFilterPage("style")}
        subText={styleSearchValues
          .map((searchValue) => {
            const item = searchValue.split("&")[0]?.trim();
            return item === "ipa" ? "IPA" : item;
          })
          .join(", ")}
      />
      <FilterDrawerItem
        title={"CONTAINER"}
        onClick={() => setFilterPage("container")}
        subText={selectedContainerTypes.join(", ")}
      />
      <FilterDrawerItem
        title={"SUPPLIER"}
        onClick={() => setFilterPage("supplier")}
        subText={selectedSuppliers.map((supplier) => supplier.name).join(", ")}
      />
      <FilterDrawerItem
        title={"PRICE"}
        onClick={() => setFilterPage("price")}
        subText={selectedPriceTypes
          .map((priceType) =>
            priceTypeCategory === "draught"
              ? draughtPriceFilterType[priceType].name
              : packagedPriceFilterType[priceType].name,
          )
          .join(", ")}
      />
      <FilterDrawerItem
        title={"ABV"}
        onClick={() => setFilterPage("abv")}
        subText={
          abvRange
            ? getRangeLabel(abvRange, (value) => `${value}%`)
            : selectedAbvTypes
                .map((abvType) => abvFilterType[abvType as AbvType].name)
                .join(", ")
        }
      />
      <FilterDrawerItem
        title={"DIETARY"}
        onClick={() => setFilterPage("dietary")}
        subText={selectedDietaryTypes
          .map((dietaryType) => dietaryTypes[dietaryType])
          .join(", ")}
      />
    </div>
  );
};
const FilterDrawerItem = ({
  title,
  subText,
  onClick,
}: {
  title: string;
  subText?: string;
  onClick: () => void;
}) => {
  return (
    <div
      className={"border-b border-solid border-gray-300 p-4"}
      onClick={onClick}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-col">
          <p className={"text-sm font-semibold"}>{title.toUpperCase()}</p>
          <p
            className={
              "overflow-hidden text-xs text-ellipsis whitespace-nowrap text-zinc-500 capitalize"
            }
          >
            {subText}
          </p>
        </div>
        <Icon icon={"CHEVRON_RIGHT"} />
      </div>
    </div>
  );
};

export const FilterDrawerHeader = ({
  title,
  icon = "CHEVRON_LEFT",
  onClick,
}: {
  title: string;
  icon?: IconType;
  onClick?: () => void;
}) => {
  return (
    <div
      className={
        "sticky top-0 z-10 flex flex-row items-center gap-2 border-b border-solid border-gray-300 bg-white p-4"
      }
    >
      <Icon onClick={onClick} className={"font-bold"} icon={icon} />
      <H5>{title}</H5>
    </div>
  );
};
